// src/components/ecommerce/InventoryManager.jsx
import React, { useState, useEffect } from 'react';
import { 
  Package,
  AlertTriangle,
  TrendingDown,
  TrendingUp,
  Plus,
  Minus,
  Search,
  Filter,
  Download,
  Upload,
  RefreshCw,
  Eye,
  Edit,
  Clock,
  Mail,
  Phone,
  Truck,
  Building,
  User,
  FileText,
  BarChart3,
  Calendar,
  Target,
  Settings,
  Bell,
  CheckCircle,
  XCircle,
  Archive,
  RotateCcw,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Zap,
  DollarSign,
  Star,
  Tag,
  Globe,
  Save,
  X,
  Loader2,
  AlertCircle,
  ShoppingCart,
  History
} from 'lucide-react';
import { ecommerceService } from '../../services/ecommerceService';
import { InventoryMovementModal } from './InventoryMovementModal';
import { StockAdjustmentModal } from './StockAdjustmentModal';
import { SupplierModal } from './SupplierModal';

export const InventoryManager = ({ storeId, storeData }) => {
  const [inventory, setInventory] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [movements, setMovements] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Filters and Search
  const [searchQuery, setSearchQuery] = useState('');
  const [stockFilter, setStockFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [sortBy, setSortBy] = useState('quantity');
  const [sortOrder, setSortOrder] = useState('asc');
  
  // Modals
  const [showMovementModal, setShowMovementModal] = useState(false);
  const [showAdjustmentModal, setShowAdjustmentModal] = useState(false);
  const [showSupplierModal, setShowSupplierModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  
  // Stats
  const [inventoryStats, setInventoryStats] = useState({});

  useEffect(() => {
    loadInventoryData();
  }, [storeId]);

  const loadInventoryData = async () => {
    setLoading(true);
    try {
      // Load inventory with current stock levels
      const inventoryData = await ecommerceService.getInventory(storeId);
      setInventory(inventoryData);
      
      // Load suppliers
      const suppliersData = await ecommerceService.getSuppliers(storeId);
      setSuppliers(suppliersData);
      
      // Load recent movements
      const movementsData = await ecommerceService.getInventoryMovements(storeId, 50);
      setMovements(movementsData);
      
      // Load alerts
      const alertsData = await ecommerceService.getInventoryAlerts(storeId);
      setAlerts(alertsData);
      
      // Load stats
      const stats = await ecommerceService.getInventoryStats(storeId);
      setInventoryStats(stats);
      
    } catch (error) {
      console.error('Error loading inventory data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStockAdjustment = (product) => {
    setSelectedProduct(product);
    setShowAdjustmentModal(true);
  };

  const handleMovementView = (product) => {
    setSelectedProduct(product);
    setShowMovementModal(true);
  };

  const handleSupplierEdit = (supplier = null) => {
    setSelectedSupplier(supplier);
    setShowSupplierModal(true);
  };

  const getStockStatus = (product) => {
    const quantity = product.quantity || 0;
    const threshold = product.lowStockThreshold || storeData.inventory?.lowStockThreshold || 5;
    
    if (quantity === 0) {
      return { status: 'out', label: 'Out of Stock', color: 'text-red-600 bg-red-100', icon: XCircle };
    } else if (quantity <= threshold) {
      return { status: 'low', label: 'Low Stock', color: 'text-orange-600 bg-orange-100', icon: AlertTriangle };
    } else if (quantity <= threshold * 2) {
      return { status: 'medium', label: 'Medium Stock', color: 'text-yellow-600 bg-yellow-100', icon: Package };
    } else {
      return { status: 'good', label: 'Good Stock', color: 'text-green-600 bg-green-100', icon: CheckCircle };
    }
  };

  const getStockValue = (product) => {
    const quantity = product.quantity || 0;
    const cost = product.cost || product.price || 0;
    return quantity * cost;
  };

  const filteredInventory = inventory.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         product.sku?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const stockStatus = getStockStatus(product);
    const matchesStock = stockFilter === 'all' || 
                        (stockFilter === 'low' && (stockStatus.status === 'low' || stockStatus.status === 'out')) ||
                        (stockFilter === 'out' && stockStatus.status === 'out') ||
                        (stockFilter === 'good' && stockStatus.status === 'good');
    
    const matchesCategory = categoryFilter === 'all' || product.category === categoryFilter;
    
    return matchesSearch && matchesStock && matchesCategory;
  });

  const sortedInventory = [...filteredInventory].sort((a, b) => {
    let comparison = 0;
    
    switch (sortBy) {
      case 'name':
        comparison = a.name.localeCompare(b.name);
        break;
      case 'quantity':
        comparison = (a.quantity || 0) - (b.quantity || 0);
        break;
      case 'value':
        comparison = getStockValue(a) - getStockValue(b);
        break;
      case 'lastMovement':
        comparison = new Date(a.lastMovement || 0) - new Date(b.lastMovement || 0);
        break;
      default:
        comparison = 0;
    }
    
    return sortOrder === 'asc' ? comparison : -comparison;
  });

  const tabs = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'products', label: 'Stock Levels', icon: Package },
    { id: 'movements', label: 'Movements', icon: History },
    { id: 'suppliers', label: 'Suppliers', icon: Building },
    { id: 'alerts', label: 'Alerts', icon: Bell, badge: alerts.length }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Inventory Management</h2>
          <p className="text-gray-600">Monitor stock levels, movements, and supply chain</p>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={() => ecommerceService.exportInventory(storeId)}
            className="flex items-center gap-2 px-4 py-2 text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <Download className="h-4 w-4" />
            Export
          </button>

          <button
            onClick={loadInventoryData}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <div className="flex overflow-x-auto">
          {tabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-6 py-3 border-b-2 whitespace-nowrap transition-colors relative ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600 bg-blue-50'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                <Icon className="h-4 w-4" />
                <span className="font-medium">{tab.label}</span>
                {tab.badge && tab.badge > 0 && (
                  <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full">
                    {tab.badge}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab Content */}
      <div>
        {activeTab === 'overview' && (
          <InventoryOverview 
            stats={inventoryStats}
            inventory={inventory}
            alerts={alerts}
            movements={movements}
            onRefresh={loadInventoryData}
          />
        )}

        {activeTab === 'products' && (
          <StockLevels
            inventory={sortedInventory}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            stockFilter={stockFilter}
            setStockFilter={setStockFilter}
            categoryFilter={categoryFilter}
            setCategoryFilter={setCategoryFilter}
            sortBy={sortBy}
            setSortBy={setSortBy}
            sortOrder={sortOrder}
            setSortOrder={setSortOrder}
            onStockAdjustment={handleStockAdjustment}
            onMovementView={handleMovementView}
            getStockStatus={getStockStatus}
            getStockValue={getStockValue}
            loading={loading}
          />
        )}

        {activeTab === 'movements' && (
          <MovementsHistory
            movements={movements}
            inventory={inventory}
            onRefresh={loadInventoryData}
          />
        )}

        {activeTab === 'suppliers' && (
          <SuppliersManagement
            suppliers={suppliers}
            onEdit={handleSupplierEdit}
            onRefresh={loadInventoryData}
          />
        )}

        {activeTab === 'alerts' && (
          <AlertsManagement
            alerts={alerts}
            inventory={inventory}
            onRefresh={loadInventoryData}
            onStockAdjustment={handleStockAdjustment}
          />
        )}
      </div>

      {/* Modals */}
      {showMovementModal && selectedProduct && (
        <InventoryMovementModal
          product={selectedProduct}
          isOpen={showMovementModal}
          onClose={() => {
            setShowMovementModal(false);
            setSelectedProduct(null);
          }}
          onUpdate={loadInventoryData}
        />
      )}

      {showAdjustmentModal && selectedProduct && (
        <StockAdjustmentModal
          product={selectedProduct}
          suppliers={suppliers}
          isOpen={showAdjustmentModal}
          onClose={() => {
            setShowAdjustmentModal(false);
            setSelectedProduct(null);
          }}
          onUpdate={loadInventoryData}
        />
      )}

      {showSupplierModal && (
        <SupplierModal
          supplier={selectedSupplier}
          storeId={storeId}
          isOpen={showSupplierModal}
          onClose={() => {
            setShowSupplierModal(false);
            setSelectedSupplier(null);
          }}
          onUpdate={loadInventoryData}
        />
      )}
    </div>
  );
};

// Inventory Overview Component
const InventoryOverview = ({ stats, inventory, alerts, movements, onRefresh }) => {
  const totalProducts = inventory.length;
  const totalValue = inventory.reduce((sum, item) => sum + ((item.quantity || 0) * (item.cost || item.price || 0)), 0);
  const lowStockItems = inventory.filter(item => (item.quantity || 0) <= (item.lowStockThreshold || 5)).length;
  const outOfStockItems = inventory.filter(item => (item.quantity || 0) === 0).length;
  const topMovingItems = movements.slice(0, 5);

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Products</p>
              <p className="text-2xl font-bold text-gray-900">{totalProducts}</p>
              <p className="text-sm text-blue-600">In inventory</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-lg">
              <Package className="h-6 w-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Value</p>
              <p className="text-2xl font-bold text-gray-900">R{totalValue.toLocaleString()}</p>
              <p className="text-sm text-green-600">Stock value</p>
            </div>
            <div className="p-3 bg-green-100 rounded-lg">
              <DollarSign className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Low Stock</p>
              <p className="text-2xl font-bold text-gray-900">{lowStockItems}</p>
              <p className="text-sm text-orange-600">Need attention</p>
            </div>
            <div className="p-3 bg-orange-100 rounded-lg">
              <AlertTriangle className="h-6 w-6 text-orange-600" />
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Out of Stock</p>
              <p className="text-2xl font-bold text-gray-900">{outOfStockItems}</p>
              <p className="text-sm text-red-600">Urgent restock</p>
            </div>
            <div className="p-3 bg-red-100 rounded-lg">
              <XCircle className="h-6 w-6 text-red-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button className="p-4 border-2 border-dashed border-blue-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors text-center">
            <Plus className="h-8 w-8 text-blue-500 mx-auto mb-2" />
            <span className="text-sm font-medium text-blue-700">Bulk Stock Update</span>
          </button>
          <button className="p-4 border-2 border-dashed border-green-300 rounded-lg hover:border-green-500 hover:bg-green-50 transition-colors text-center">
            <Building className="h-8 w-8 text-green-500 mx-auto mb-2" />
            <span className="text-sm font-medium text-green-700">Create Purchase Order</span>
          </button>
          <button className="p-4 border-2 border-dashed border-purple-300 rounded-lg hover:border-purple-500 hover:bg-purple-50 transition-colors text-center">
            <Download className="h-8 w-8 text-purple-500 mx-auto mb-2" />
            <span className="text-sm font-medium text-purple-700">Generate Report</span>
          </button>
        </div>
      </div>

      {/* Recent Activity & Alerts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Recent Movements</h3>
              <button 
                onClick={onRefresh}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
              >
                <RefreshCw className="h-4 w-4" />
              </button>
            </div>
          </div>
          <div className="p-6">
            {movements.length > 0 ? (
              <div className="space-y-4">
                {movements.slice(0, 5).map((movement, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className={`p-2 rounded-lg ${
                      movement.type === 'in' ? 'bg-green-100' : 
                      movement.type === 'out' ? 'bg-red-100' : 'bg-blue-100'
                    }`}>
                      {movement.type === 'in' ? (
                        <ArrowUp className="h-4 w-4 text-green-600" />
                      ) : movement.type === 'out' ? (
                        <ArrowDown className="h-4 w-4 text-red-600" />
                      ) : (
                        <RotateCcw className="h-4 w-4 text-blue-600" />
                      )}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">
                        {movement.productName}
                      </p>
                      <p className="text-xs text-gray-500">
                        {movement.type === 'in' ? '+' : movement.type === 'out' ? '-' : '±'}{movement.quantity} units
                        {movement.reason && ` • ${movement.reason}`}
                      </p>
                      <p className="text-xs text-gray-400">
                        {new Date(movement.createdAt?.toDate?.() || movement.createdAt).toLocaleString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <History className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">No recent movements</p>
              </div>
            )}
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Inventory Alerts</h3>
          </div>
          <div className="p-6">
            {alerts.length > 0 ? (
              <div className="space-y-4">
                {alerts.slice(0, 5).map((alert, index) => (
                  <div key={index} className={`p-3 rounded-lg border ${
                    alert.severity === 'high' ? 'bg-red-50 border-red-200' :
                    alert.severity === 'medium' ? 'bg-orange-50 border-orange-200' :
                    'bg-yellow-50 border-yellow-200'
                  }`}>
                    <div className="flex items-start gap-3">
                      <AlertTriangle className={`h-4 w-4 mt-0.5 ${
                        alert.severity === 'high' ? 'text-red-600' :
                        alert.severity === 'medium' ? 'text-orange-600' :
                        'text-yellow-600'
                      }`} />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">
                          {alert.title}
                        </p>
                        <p className="text-xs text-gray-600">
                          {alert.description}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <CheckCircle className="h-12 w-12 text-green-400 mx-auto mb-4" />
                <p className="text-gray-600">No active alerts</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Stock Levels Component
const StockLevels = ({ 
  inventory, 
  searchQuery, 
  setSearchQuery,
  stockFilter,
  setStockFilter,
  categoryFilter,
  setCategoryFilter,
  sortBy,
  setSortBy,
  sortOrder,
  setSortOrder,
  onStockAdjustment,
  onMovementView,
  getStockStatus,
  getStockValue,
  loading 
}) => {
  const categories = [...new Set(inventory.map(item => item.category).filter(Boolean))];

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search products..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          <div className="flex items-center gap-3">
            <select
              value={stockFilter}
              onChange={(e) => setStockFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Stock</option>
              <option value="good">Good Stock</option>
              <option value="low">Low Stock</option>
              <option value="out">Out of Stock</option>
            </select>

            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Categories</option>
              {categories.map(category => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>

            <select
              value={`${sortBy}-${sortOrder}`}
              onChange={(e) => {
                const [sort, order] = e.target.value.split('-');
                setSortBy(sort);
                setSortOrder(order);
              }}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="quantity-asc">Stock Low-High</option>
              <option value="quantity-desc">Stock High-Low</option>
              <option value="name-asc">Name A-Z</option>
              <option value="value-desc">Value High-Low</option>
              <option value="lastMovement-desc">Recent Activity</option>
            </select>
          </div>
        </div>
      </div>

      {/* Products Table */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      ) : inventory.length > 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Product
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Current Stock
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Value
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Movement
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {inventory.map(product => {
                const stockStatus = getStockStatus(product);
                const StatusIcon = stockStatus.icon;
                const stockValue = getStockValue(product);
                
                return (
                  <tr key={product.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div className="h-10 w-10 flex-shrink-0">
                          {product.images?.[0] || product.imageUrl ? (
                            <img
                              src={product.images?.[0] || product.imageUrl}
                              alt={product.name}
                              className="h-10 w-10 object-cover rounded-lg"
                            />
                          ) : (
                            <div className="h-10 w-10 bg-gray-200 rounded-lg flex items-center justify-center">
                              <Package className="h-5 w-5 text-gray-400" />
                            </div>
                          )}
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {product.name}
                          </div>
                          <div className="text-sm text-gray-500">
                            SKU: {product.sku}
                          </div>
                        </div>
                      </div>
                    </td>

                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">
                        {product.quantity || 0} units
                      </div>
                      <div className="text-xs text-gray-500">
                        Threshold: {product.lowStockThreshold || 5}
                      </div>
                    </td>

                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-full ${stockStatus.color}`}>
                        <StatusIcon className="h-3 w-3" />
                        {stockStatus.label}
                      </span>
                    </td>

                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">
                        R{stockValue.toFixed(2)}
                      </div>
                      <div className="text-xs text-gray-500">
                        @R{(product.cost || product.price || 0).toFixed(2)}
                      </div>
                    </td>

                    <td className="px-6 py-4">
                      {product.lastMovement ? (
                        <>
                          <div className="text-sm text-gray-900">
                            {new Date(product.lastMovement).toLocaleDateString()}
                          </div>
                          <div className="text-xs text-gray-500">
                            {product.lastMovementType}
                          </div>
                        </>
                      ) : (
                        <span className="text-sm text-gray-500">No movements</span>
                      )}
                    </td>

                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => onStockAdjustment(product)}
                          className="p-1 text-gray-400 hover:text-blue-600"
                          title="Adjust Stock"
                        >
                          <Edit className="h-4 w-4" />
                        </button>

                        <button
                          onClick={() => onMovementView(product)}
                          className="p-1 text-gray-400 hover:text-green-600"
                          title="View Movements"
                        >
                          <History className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center py-12">
          <Package className="mx-auto h-16 w-16 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No inventory found</h3>
          <p className="text-gray-600">
            {searchQuery || stockFilter !== 'all' || categoryFilter !== 'all'
              ? 'Try adjusting your search or filters'
              : 'Products will appear here once you add them to your store'
            }
          </p>
        </div>
      )}
    </div>
  );
};

// MovementsHistory Component
const MovementsHistory = ({ movements, inventory, onRefresh }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');

  const getMovementIcon = (type) => {
    switch (type) {
      case 'in': return ArrowUp;
      case 'out': return ArrowDown;
      case 'adjustment': return RotateCcw;
      default: return History;
    }
  };

  const getMovementColor = (type) => {
    switch (type) {
      case 'in': return 'text-green-600 bg-green-100';
      case 'out': return 'text-red-600 bg-red-100';
      case 'adjustment': return 'text-blue-600 bg-blue-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const filteredMovements = movements.filter(movement => {
    const matchesSearch = movement.productName?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = typeFilter === 'all' || movement.type === typeFilter;
    const matchesDate = dateFilter === 'all' || (() => {
      const movementDate = new Date(movement.createdAt?.toDate?.() || movement.createdAt);
      const now = new Date();
      
      switch (dateFilter) {
        case 'today':
          return movementDate.toDateString() === now.toDateString();
        case 'week':
          const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          return movementDate >= weekAgo;
        case 'month':
          const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          return movementDate >= monthAgo;
        default:
          return true;
      }
    })();
    
    return matchesSearch && matchesType && matchesDate;
  });

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold text-gray-900">Movement History</h3>
        <button
          onClick={onRefresh}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <RefreshCw className="h-4 w-4" />
          Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search movements..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div className="flex gap-3">
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Types</option>
              <option value="in">Stock In</option>
              <option value="out">Stock Out</option>
              <option value="adjustment">Adjustment</option>
            </select>

            <select
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Time</option>
              <option value="today">Today</option>
              <option value="week">Last 7 Days</option>
              <option value="month">Last 30 Days</option>
            </select>
          </div>
        </div>
      </div>

      {/* Movements List */}
      <div className="bg-white rounded-lg border border-gray-200">
        {filteredMovements.length > 0 ? (
          <div className="divide-y divide-gray-200">
            {filteredMovements.map(movement => {
              const MovementIcon = getMovementIcon(movement.type);
              const colorClasses = getMovementColor(movement.type);
              
              return (
                <div key={movement.id} className="p-4 hover:bg-gray-50">
                  <div className="flex items-center gap-4">
                    <div className={`p-2 rounded-lg ${colorClasses}`}>
                      <MovementIcon className="h-5 w-5" />
                    </div>

                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <h4 className="font-medium text-gray-900">{movement.productName}</h4>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${colorClasses}`}>
                          {movement.type}
                        </span>
                      </div>
                      
                      <div className="flex items-center gap-4 mt-1 text-sm text-gray-600">
                        <span>SKU: {movement.sku}</span>
                        <span>•</span>
                        <span>{movement.type === 'in' ? '+' : movement.type === 'out' ? '-' : '±'}{movement.quantity} units</span>
                        <span>•</span>
                        <span>{new Date(movement.createdAt?.toDate?.() || movement.createdAt).toLocaleDateString()}</span>
                      </div>

                      {movement.reason && (
                        <p className="text-sm text-gray-500 mt-1">{movement.reason}</p>
                      )}
                    </div>

                    <div className="text-right">
                      <div className="text-sm font-medium text-gray-900">
                        By: {movement.createdBy || 'System'}
                      </div>
                      <div className="text-xs text-gray-500">
                        {new Date(movement.createdAt?.toDate?.() || movement.createdAt).toLocaleTimeString()}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-12">
            <History className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No movements found</h3>
            <p className="text-gray-600">
              {searchQuery || typeFilter !== 'all' || dateFilter !== 'all'
                ? 'Try adjusting your search or filters'
                : 'Inventory movements will appear here'
              }
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

// SuppliersManagement Component
const SuppliersManagement = ({ suppliers, onEdit, onRefresh }) => {
  const [searchQuery, setSearchQuery] = useState('');

  const filteredSuppliers = suppliers.filter(supplier =>
    supplier.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    supplier.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold text-gray-900">Suppliers</h3>
        <div className="flex gap-3">
          <button
            onClick={() => onEdit()}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus className="h-4 w-4" />
            Add Supplier
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search suppliers..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      {/* Suppliers Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredSuppliers.length > 0 ? (
          filteredSuppliers.map(supplier => (
            <div key={supplier.id} className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Building className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">{supplier.name}</h4>
                    <p className="text-sm text-gray-500">{supplier.category || 'General'}</p>
                  </div>
                </div>
                <button
                  onClick={() => onEdit(supplier)}
                  className="p-1 text-gray-400 hover:text-blue-600"
                >
                  <Edit className="h-4 w-4" />
                </button>
              </div>

              <div className="space-y-2 text-sm">
                {supplier.contact && (
                  <div className="flex items-center gap-2 text-gray-600">
                    <User className="h-4 w-4" />
                    <span>{supplier.contact}</span>
                  </div>
                )}
                {supplier.email && (
                  <div className="flex items-center gap-2 text-gray-600">
                    <Mail className="h-4 w-4" />
                    <span>{supplier.email}</span>
                  </div>
                )}
                {supplier.phone && (
                  <div className="flex items-center gap-2 text-gray-600">
                    <Phone className="h-4 w-4" />
                    <span>{supplier.phone}</span>
                  </div>
                )}
                {supplier.address && (
                  <div className="flex items-center gap-2 text-gray-600">
                    <Building className="h-4 w-4" />
                    <span className="truncate">{supplier.address}</span>
                  </div>
                )}
              </div>

              <div className="mt-4 pt-4 border-t border-gray-100">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Products Supplied:</span>
                  <span className="font-medium">{supplier.productCount || 0}</span>
                </div>
                <div className="flex items-center justify-between text-sm mt-1">
                  <span className="text-gray-600">Last Order:</span>
                  <span className="font-medium">
                    {supplier.lastOrderDate 
                      ? new Date(supplier.lastOrderDate).toLocaleDateString()
                      : 'Never'
                    }
                  </span>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="col-span-full text-center py-12">
            <Building className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No suppliers found</h3>
            <p className="text-gray-600 mb-6">
              {searchQuery 
                ? 'Try adjusting your search terms'
                : 'Add suppliers to manage your supply chain'
              }
            </p>
            <button
              onClick={() => onEdit()}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Plus className="h-4 w-4" />
              Add First Supplier
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// AlertsManagement Component
const AlertsManagement = ({ alerts, inventory, onRefresh, onStockAdjustment }) => {
  const [filter, setFilter] = useState('all');

  const filteredAlerts = alerts.filter(alert => {
    if (filter === 'all') return true;
    return alert.severity === filter;
  });

  const getAlertIcon = (type) => {
    switch (type) {
      case 'low_stock': return AlertTriangle;
      case 'out_of_stock': return XCircle;
      case 'overstocked': return Package;
      case 'expired': return Clock;
      default: return AlertCircle;
    }
  };

  const getAlertColor = (severity) => {
    switch (severity) {
      case 'high': return 'border-red-200 bg-red-50 text-red-800';
      case 'medium': return 'border-orange-200 bg-orange-50 text-orange-800';
      case 'low': return 'border-yellow-200 bg-yellow-50 text-yellow-800';
      default: return 'border-gray-200 bg-gray-50 text-gray-800';
    }
  };

  const handleResolveAlert = async (alertId) => {
    try {
      // Logic to resolve alert
      await onRefresh();
    } catch (error) {
      console.error('Error resolving alert:', error);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Inventory Alerts</h3>
          <p className="text-gray-600">Monitor and resolve inventory issues</p>
        </div>
        <button
          onClick={onRefresh}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <RefreshCw className="h-4 w-4" />
          Refresh
        </button>
      </div>

      {/* Filter */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex gap-3">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Alerts</option>
            <option value="high">High Priority</option>
            <option value="medium">Medium Priority</option>
            <option value="low">Low Priority</option>
          </select>
        </div>
      </div>

      {/* Alerts List */}
      <div className="space-y-4">
        {filteredAlerts.length > 0 ? (
          filteredAlerts.map(alert => {
            const AlertIcon = getAlertIcon(alert.type);
            const colorClasses = getAlertColor(alert.severity);
            
            return (
              <div key={alert.id} className={`border rounded-lg p-4 ${colorClasses}`}>
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 mt-1">
                    <AlertIcon className="h-5 w-5" />
                  </div>
                  
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <h4 className="font-semibold">{alert.title}</h4>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          alert.severity === 'high' ? 'bg-red-100 text-red-800' :
                          alert.severity === 'medium' ? 'bg-orange-100 text-orange-800' :
                          'bg-yellow-100 text-yellow-800'
                        }`}>
                          {alert.severity} priority
                        </span>
                      </div>
                    </div>
                    
                    <p className="mt-1 text-sm">{alert.description}</p>
                    
                    <div className="mt-3 flex gap-2">
                      {alert.type === 'low_stock' || alert.type === 'out_of_stock' ? (
                        <button
                          onClick={() => {
                            const product = inventory.find(p => p.id === alert.productId);
                            if (product) onStockAdjustment(product);
                          }}
                          className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                        >
                          Adjust Stock
                        </button>
                      ) : null}
                      
                      <button
                        onClick={() => handleResolveAlert(alert.id)}
                        className="px-3 py-1 text-sm border border-current rounded hover:bg-black hover:bg-opacity-10"
                      >
                        Mark Resolved
                      </button>
                    </div>
                    
                    <div className="mt-2 text-xs opacity-75">
                      {new Date(alert.createdAt?.toDate?.() || alert.createdAt).toLocaleString()}
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <div className="text-center py-12">
            <CheckCircle className="h-12 w-12 text-green-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {filter === 'all' ? 'No active alerts' : `No ${filter} priority alerts`}
            </h3>
            <p className="text-gray-600">
              {filter === 'all' 
                ? 'Your inventory is running smoothly'
                : `No ${filter} priority issues to address`
              }
            </p>
          </div>
        )}
      </div>
    </div>
  );
};